import React, { useCallback, useState } from 'react';
import { PRIVACY_POLICY_URL, TOS_GENERAL_URL } from '../../../constants';
import { TosType } from '../../../ReduxStorage/Application/types';
import { UserTosStatus } from '../../../ReduxStorage/LoggedInUser/types';
import { SnackBar, SnackBarButton } from '../SnackBar/component';

export interface TosSnackbarProps {
  userGeneralTosStatus: UserTosStatus;
  currentUserId: string;
  isTosUpdateEnabled: boolean;
  acceptTermsOfService: (tosType: TosType) => void;
}

const ONE_DAY_TIMEOUT_PERIOD_IN_MS = 24 * 60 * 60 * 1000;
const TOS_REMIND_ME_LATER_DATETIME = 'tos_remind_me_later_datetime';

export const TosSnackbar: React.FC<TosSnackbarProps> = ({
  userGeneralTosStatus,
  currentUserId,
  isTosUpdateEnabled,
  acceptTermsOfService,
}) => {
  const [isRemindMeLaterPressed, setIsRemindMeLaterPressed] = useState(false);

  const onAcceptTosButton = useCallback(
    () => acceptTermsOfService('general'),
    []
  );

  const onRemindMeLaterClicked = useCallback(() => {
    const nowDate = new Date();
    const next24HoursInMs = new Date(
      nowDate.getTime() + ONE_DAY_TIMEOUT_PERIOD_IN_MS
    ).getTime();
    // localStorage key need to have the current user id, so the dismiss function will be applied per user
    localStorage.setItem(
      `${TOS_REMIND_ME_LATER_DATETIME}_${currentUserId}`,
      next24HoursInMs.toString()
    );
    setIsRemindMeLaterPressed(true);
  }, [currentUserId]);

  const isNowPastRemindLaterDateTime = (): boolean => {
    const remindLaterDateTime = localStorage.getItem(
      `${TOS_REMIND_ME_LATER_DATETIME}_${currentUserId}`
    );

    // if user already clicked "Remind me later", then compare current datetime with remind later stored date time
    if (remindLaterDateTime) {
      return new Date().getTime() > parseInt(remindLaterDateTime, 10);
    }
    // user hasn't clicked remind me later, show tos snackbar to the user
    return true;
  };
  const showTosSnackbar =
    isTosUpdateEnabled &&
    (userGeneralTosStatus === UserTosStatus.NOT_ACCEPTED ||
      userGeneralTosStatus === UserTosStatus.OUTDATED) &&
    isNowPastRemindLaterDateTime() &&
    !isRemindMeLaterPressed;

  return (
    <>
      {showTosSnackbar && (
        <SnackBar
          buttons={[
            <SnackBarButton onClick={onAcceptTosButton} primary>
              OK
            </SnackBarButton>,
            <SnackBarButton onClick={onRemindMeLaterClicked}>
              Remind me later
            </SnackBarButton>,
          ]}
        >
          {userGeneralTosStatus === UserTosStatus.NOT_ACCEPTED ? (
            <>
              By continuing to use OpenLearning, you agree to our{' '}
              <a
                href={TOS_GENERAL_URL}
                target="_blank"
                rel="noopener noreferrer"
              >
                Terms of service
              </a>{' '}
              and{' '}
              <a
                href={PRIVACY_POLICY_URL}
                target="_blank"
                rel="noopener noreferrer"
              >
                Privacy policy
              </a>
              .
            </>
          ) : (
            <>
              We have updated our{' '}
              <a
                href={TOS_GENERAL_URL}
                target="_blank"
                rel="noopener noreferrer"
              >
                Terms of service
              </a>{' '}
              and{' '}
              <a
                href={PRIVACY_POLICY_URL}
                target="_blank"
                rel="noopener noreferrer"
              >
                Privacy policy
              </a>
              . By continuing to use OpenLearning, you agree to the updated
              terms and policies.
            </>
          )}
        </SnackBar>
      )}
    </>
  );
};
